import DocumentViewerModal from "components/DocumentViewer";
import { StatusChip } from "components/StatusChip";
import { useTranslation } from "contexts/i18nContext";
import { useHasRole } from "contexts/AuthContext";
import { useState } from "react";
import {
  Appeal,
  Complaints,
  CouncilLitigation,
  CouncilOfficeVerdictInitialDay,
} from "types";
import { APPEAL_STATUS, APPEAL_TYPE } from "utils/constantValues";
import { ROLES } from "utils/roles";
import AddAppealFeedbackModal from "./AddAppealFeedbackModal";
import AddAppealRegistrationModal from "./AddAppealRegistrationModal";
import { useAuth } from "contexts/AuthContext";

interface AppealProps {
  complaintData: Complaints;
  appealData: Appeal[];
  councilLitigationData?: CouncilLitigation[];
  onAppealRefresh: any;
}

export default function AppealCard({
  complaintData,
  appealData,
  councilLitigationData,
  onAppealRefresh,
}: AppealProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const canCreateAppealFeedback = useHasRole([
    ROLES.INSPECTION_DIRECTOR,
    ROLES.INSPECTION_COORDINATOR,
  ]);
  const canViewAppealFeedback = useHasRole([
    ROLES.INSPECTION_DIRECTOR,
    ROLES.INSPECTION_COORDINATOR,
    ROLES.INSPECTION_PROSECUTOR,
  ]);
  const canRegisterAppealDoc = useHasRole([ROLES.INSPECTION_REGISTRAR]);

  const [isOpen, setIsOpen] = useState(false);
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  const [selectedAppeal, setSelectedAppeal] = useState({});

  return (
    <div className="space-y-4">
      {/* Initial Day Verdicts */}
      {councilLitigationData?.[0]?.initial_day_verdicts?.map(
        (obj: CouncilOfficeVerdictInitialDay) => (
          <div
            key={obj?.id}
            className="bg-white shadow-md rounded-xl p-4 w-full border-l-8 border-black mx-auto"
          >
            <div className="mt-2 border rounded-md p-3 bg-gray-50">
              <p className="text-[#002855] font-medium">Initial Day Verdict</p>
              <div className="flex flex-wrap gap-2 mb-3">
                {obj?.verdict_given_on_intialday_ins && (
                  <span className="px-2 py-1 text-xs rounded bg-green-100 text-green-700 border border-green-200">
                    Institution
                  </span>
                )}
                {obj?.verdict_given_on_intialday_accused && (
                  <span className="px-2 py-1 text-xs rounded bg-indigo-100 text-indigo-700 border border-indigo-200">
                    Accused
                  </span>
                )}
              </div>
              {obj?.council_decision_day && (
                <div className="w-full flex flex-wrap items-center justify-between gap-4 my-2">
                  <p className="block text-gray-600">Decision Day</p>
                  <p className="block text-black font-medium">
                    {(() => {
                      try {
                        const d = new Date(obj?.council_decision_day as any);
                        return isNaN(d.getTime())
                          ? (obj?.council_decision_day as any)
                          : d.toDateString();
                      } catch {
                        return (obj?.council_decision_day as any) || "-";
                      }
                    })()}
                  </p>
                </div>
              )}
              {obj?.remaining_days_for_appeal !== undefined &&
                obj?.remaining_days_for_appeal !== null && (
                  <div className="w-full flex flex-wrap items-center justify-between gap-4 my-2">
                    <p className="block text-gray-600">
                      Remaining Days For Appeal
                    </p>
                    <p className="block text-black font-medium">
                      {obj?.remaining_days_for_appeal}
                    </p>
                  </div>
                )}
              {obj?.given_order && (
                <div className="w-full flex flex-wrap items-start justify-between gap-4 my-2">
                  <p className="block text-gray-600">Given Order</p>
                  <p className="block text-black text-sm max-w-md whitespace-pre-line">
                    {obj?.given_order}
                  </p>
                </div>
              )}
              {obj?.council_decision_type && (
                <div className="w-full flex flex-wrap items-center justify-between gap-4 my-2">
                  <p className="block text-gray-600">Decision Type</p>
                  <p className="block text-black font-medium">
                    {obj?.council_decision_type}
                  </p>
                </div>
              )}
              {obj?.initial_day_council_decision_files && (
                <div className="w-full flex flex-wrap items-center justify-between gap-4 my-2">
                  <p className="block text-gray-600">Decision File</p>
                  <p
                    className="block text-black text-sm font-medium truncate max-w-xs"
                    title={obj?.initial_day_council_decision_files?.name}
                  >
                    {obj?.initial_day_council_decision_files?.name || "File"}
                  </p>
                </div>
              )}
            </div>

            <div className="my-3">
              <p className="text-[#002855] font-medium">
                Council Decision Summary
              </p>
              <p className="text-gray-700 mt-1">
                {obj?.council_decision_summary}
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-1 gap-3">
              {obj?.accused_prosecutor_name && (
                <div className="flex items-center justify-between">
                  <p className="text-gray-600">Accused Prosecutor</p>
                  <p className="text-black font-medium">
                    {obj.accused_prosecutor_name}
                  </p>
                </div>
              )}

              {obj?.appeal_request_ins_prosecutor && (
                <div className="flex items-center justify-between">
                  <p className="text-gray-600">Appeal Request</p>
                  <p
                    className="text-black font-medium truncate max-w-[60%]"
                    title={
                      typeof obj.appeal_request_ins_prosecutor === "object"
                        ? (obj.appeal_request_ins_prosecutor as any)
                            ?.registration_number ||
                          (obj.appeal_request_ins_prosecutor as any)?.id
                        : String(obj.appeal_request_ins_prosecutor)
                    }
                  >
                    {typeof obj.appeal_request_ins_prosecutor === "object"
                      ? (obj.appeal_request_ins_prosecutor as any)
                          ?.registration_number ||
                        (obj.appeal_request_ins_prosecutor as any)?.id ||
                        "-"
                      : String(obj.appeal_request_ins_prosecutor)}
                  </p>
                </div>
              )}
            </div>

            {Array.isArray(obj?.documents) && obj.documents.length > 0 && (
              <div className="mt-4">
                <DocumentViewerModal
                  documents={obj.documents as any}
                  baseUrl={`${process.env.REACT_APP_API_BASE_URL}prosecutor-decipiline/complaint-termination-documents/document`}
                />
              </div>
            )}
          </div>
        ),
      )}

      {appealData?.map((obj: any) => (
        <div
          key={obj?.id}
          className="bg-white shadow-md rounded-xl p-4 w-full border-l-8 border-black mx-auto"
        >
          {/* Header */}
          {/* <div className="flex items-center gap-3 mb-4">
          <img
            src={require("../../../images/avatar.png")}
            alt="profile"
            className="w-10 h-10 rounded-full object-cover"
          />
          <div>
            <h2 className="text-sm font-semibold">Senay Daniel</h2>
            <p className="text-xs text-gray-500">Investigation Team</p>
          </div>
        </div> */}

          {/* <div className="w-full flex flex-wrap items-center justify-between gap-4 my-2">
          <p className="block text-gray-600">Uploaded Date</p>
          <p className="block text-black font-medium">
            {new Date()?.toDateString() || "-"}
          </p>
          </div>


          {/* Info Rows */}
          <div className="my-3">
            <p className="text-[#002855] font-medium">
              {t("memorandumSummary")}
            </p>
            <p className="text-gray-700 mt-1">{obj?.memorandum_summary}</p>
          </div>

          {obj?.appeal_documents && obj?.appeal_documents.length > 0 && (
            <DocumentViewerModal
              documents={obj?.appeal_documents}
              baseUrl={`${process.env.REACT_APP_API_BASE_URL}prosecutor-decipiline/complaint-termination-documents/document`}
            />
          )}

          {/* {obj?.memorandum_details && (
            <div className="flex justify-between items-center p-2 bg-gray-50 rounded-md my-3">
              <div className="flex items-center gap-2">
                <div className="bg-[#5e3b1f] text-white font-bold text-xs px-2 py-1 rounded">
                  PDF
                </div>
                <div>
                  <p className="text-sm font-medium">
                    {obj?.memorandum_details?.file_name || t("memorandumDoc")}
                  </p>
                  <p className="text-xs text-gray-500">11.3MB</p>
                </div>
              </div>

              <FileActions fileUrl={obj?.memorandum_details?.url} />
            </div>
          )} */}

          <div className="mb-6 ">
            {obj?.appeal_type && (
              <div className="w-full flex flex-wrap items-center justify-between gap-4 my-3">
                <p className="block  text-gray-600">{t("appealType")}</p>
                <p className="block  text-black font-medium">
                  {obj?.appeal_type === APPEAL_TYPE.ACCUSED_PROSECUTOR_TO_COURT
                    ? " Appeal from Inspection Prosecutor to Court"
                    : "Appeal from Inspection Prosecutor to Council Office"}
                </p>
              </div>
            )}
            <hr />
            {obj?.registration_date && (
              <div className="w-full flex flex-wrap items-center justify-between gap-4 my-3">
                <p className="block  text-gray-600">{t("registeredDate")}</p>
                <p className="block  text-black font-medium">
                  {obj?.registration_date || "-"}
                </p>
              </div>
            )}

            {obj?.registration_number && (
              <div className="w-full flex flex-wrap items-center justify-between gap-4 my-3">
                <p className="block  text-gray-600">{t("registeredNumber")}</p>
                <p className="block  text-black font-medium">
                  {obj?.registration_number || "-"}
                </p>
              </div>
            )}
          </div>

          {obj?.if_reject_comment && (
            <div className="my-3">
              <p className="text-gray-600"></p>
              <p className="text-gray-700 mt-1">{obj?.if_reject_comment}</p>
            </div>
          )}

          {canViewAppealFeedback && obj?.feedback_on_memorandum && (
            <div className="bg-gray-200 p-5 rounded-md">
              <div className="flex items-center justify-between mb-2">
                <p className="font-medium ">{t("feedbackOnMemorandum")}</p>
                <StatusChip status={obj?.appeal_feedback_status ?? null} />
              </div>
              <p className="text-sm leading-relaxed">
                {obj?.feedback_on_memorandum || "-"}
              </p>
            </div>
          )}

          {canCreateAppealFeedback &&
            user?.admin_unit === complaintData?.admin_unit_id &&
            obj?.appeal_status !== APPEAL_STATUS.APPROVED &&
            !obj?.feedback_on_memorandum && (
              <button
                className="w-full bg-[#5f5f5f] hover:bg-gray-800 border border-white text-white py-2 rounded-lg mt-2"
                onClick={() => {
                  setSelectedAppeal(obj);
                  setIsFeedbackOpen(true);
                }}
              >
                {t("addFeedbackOnMemorandum")}
              </button>
            )}

          {canRegisterAppealDoc &&
            user?.admin_unit === complaintData?.admin_unit_id &&
            obj?.appeal_status === APPEAL_STATUS.FORWARDED_TO_REGISTRAR &&
            !obj?.registration_number && (
              <div className="text-right mt-3">
                <button
                  className="bg-black text-white px-14 py-2 rounded-md hover:bg-gray-800"
                  type="submit"
                  onClick={() => {
                    setSelectedAppeal(obj);
                    setIsOpen(true);
                  }}
                >
                  {t("addRegistrationNo")}
                </button>
              </div>
            )}

          {complaintData && (
            <AddAppealFeedbackModal
              isOpen={isFeedbackOpen}
              setIsOpen={setIsFeedbackOpen}
              complaintData={complaintData}
              appealData={selectedAppeal}
              onRefresh={onAppealRefresh}
            />
          )}

          {selectedAppeal && isOpen && (
            <AddAppealRegistrationModal
              isOpen={isOpen}
              setIsOpen={setIsOpen}
              complaintData={complaintData}
              appealData={selectedAppeal}
              onRefresh={onAppealRefresh}
            />
          )}
        </div>
      ))}
    </div>
  );
}
